'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { sendRegistrationConversionEvent } from '@/app_lib/services/Piano/pianoHelpers';
import { isSocialType, type SocialLoginTypeError } from '@/app_lib/types/accounts/signin';
import type { SocialSignInApiResponse } from '@/app_lib/types/accounts/signinApi';
import type { EventData } from '@/app_lib/types/Gtm';
import { getDeviceForTracking } from '@/app_lib/utils/deviceUtils';
import { getSessionStorageObject, removeSessionStorageItem } from '@/app_lib/utils/sessionStorageUtils';

const getSocialType = (): {
  loginMethod: EventData['loginMethod'];
  socialLoginTypeError: SocialLoginTypeError;
} => {
  const sessionStorageSocialType = getSessionStorageObject('__siSocialType');
  const rawSocialType = sessionStorageSocialType?.socialType;
  const validatedSocialType = isSocialType(rawSocialType) ? rawSocialType : null;
  return { loginMethod: validatedSocialType ?? 'social', socialLoginTypeError: validatedSocialType ?? 'Unknown' };
};

interface SocialSignInControllerProps {
  children: React.ReactNode;
  onLoginSuccess: (isRegistration: boolean) => void;
  onLoginError: (socialType: SocialLoginTypeError) => void;
  onLoadingChange: (loading: boolean) => void;
}

export const SocialSignInController = ({
  children,
  onLoginSuccess,
  onLoginError,
  onLoadingChange,
}: SocialSignInControllerProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const socialResponseId = searchParams?.get('social_response_id');

  const asyncSocialLogin = useCallback(
    async (responseId: string) => {
      const { loginMethod, socialLoginTypeError } = getSocialType();
      removeSessionStorageItem('__siSocialType');

      try {
        const res = await fetch('/api/signin/social', {
          body: JSON.stringify({ registrationSource: 'sign_in_flow', responseId }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        });
        const loginResponse: SocialSignInApiResponse = await res.json();

        if (!loginResponse.success) {
          trackEvent(
            {
              deviceType: getDeviceForTracking(),
              eventAction: 'login_attempt',
              loginMethod,
              loginSuccess: false,
              loginType: 'social',
            },
            'signInEventTracker',
          );
          onLoginError(socialLoginTypeError);
          window.history.replaceState({}, '', pathname || window.location.origin);
          return;
        }

        if (loginResponse.registration) {
          sendRegistrationConversionEvent();
        }

        trackEvent(
          {
            deviceType: getDeviceForTracking(),
            eventAction: 'login_attempt',
            loginMethod,
            loginSuccess: true,
            loginType: loginResponse.registration ? 'new_account' : 'social',
            pianoUserId: loginResponse.pianoUid,
          },
          'signInEventTracker',
        );
        onLoginSuccess(loginResponse.registration === true);
      } catch {
        trackEvent(
          {
            deviceType: getDeviceForTracking(),
            eventAction: 'login_attempt',
            loginMethod,
            loginSuccess: false,
            loginType: 'social',
          },
          'signInEventTracker',
        );
        onLoginError(socialLoginTypeError);
        window.history.replaceState({}, '', pathname || window.location.origin);
      } finally {
        onLoadingChange(false);
      }
    },
    [pathname, onLoginSuccess, onLoginError, onLoadingChange],
  );

  useEffect(() => {
    if (!window.opener) {
      return;
    }
    const responseId = searchParams?.get('response_id');
    if (!responseId) {
      return;
    }
    const target = new URL(window.location.href);
    target.searchParams.set('social_response_id', responseId);
    (window.opener as Window).location.href = target.href;
    window.close();
  }, [searchParams]);

  useEffect(() => {
    if (socialResponseId) {
      asyncSocialLogin(socialResponseId);
    }
  }, [socialResponseId, asyncSocialLogin]);

  return <>{children}</>;
};
